/**
 * Internal field-set definitions + per-event `CLERK_EVENT_METADATA`
 * catalog. Split out from `supported-events.ts` to keep that file
 * under the workspace's 1000-line per-module budget; consumers should
 * keep importing from `supported-events.ts`, which re-exports the
 * catalog.
 */

import type { InboundEventMetadata } from '@/lib/integrations/inbound/types'
import type {
  IntegrationTriggerField,
  IntegrationTriggerPayloadSchema,
} from '@/lib/triggers/types'
import {
  MEMBERSHIP_EXTRA_FIELDS,
  ORG_EXTRA_FIELDS,
  ORG_INVITATION_EXTRA_FIELDS,
  PAYMENT_ATTEMPT_FIELDS,
  SUBSCRIPTION_FIELDS,
  SUBSCRIPTION_ITEM_FIELDS,
  USER_EXTRA_FIELDS,
  WAITLIST_EXTRA_FIELDS,
} from './event-metadata-fields'
import type { SupportedClerkEventType } from './event-types'

type TriggerField = IntegrationTriggerField
type TriggerPayloadSchema = IntegrationTriggerPayloadSchema

export type ClerkSupportedEventMetadata = Omit<
  InboundEventMetadata,
  'eventType'
> & {
  eventType: SupportedClerkEventType
}

/**
 * The slim, user-facing common preamble.
 *
 * Only fields a marketer can usefully template into an email body /
 * subject line or branch a flow on land here. Internal provider metadata
 * (`svix-id`, `instance_id`, event type echoes, `objectId` / `objectType`,
 * etc.) is intentionally absent — the catalog used to list them and the
 * Test dialog asked the operator to fill them in by hand, which was the
 * direct cause of the production "Failed to fire test" bug. The
 * extractor still emits those keys into `triggerInstances.payload` (and
 * the contact mapper reads from the raw payload, NOT the schema-filtered
 * `resolvedPayload`), so dedup + contact attribution are unaffected by
 * the trim — only the user-visible surface changes.
 */
const COMMON_FIELDS: Array<TriggerField> = [
  {
    key: 'email',
    type: 'string',
    required: true,
    description:
      'Recipient email derived from the Clerk event. A synthetic placeholder marks a missing recipient; Brew resolves a previously observed identity or records the event as unresolved without starting an automation.',
    exampleValue: 'jane.doe@acme.com',
  },
  {
    key: 'firstName',
    type: 'string',
    required: false,
    description: 'First name when present on the event payload.',
    exampleValue: 'Jane',
  },
  {
    key: 'lastName',
    type: 'string',
    required: false,
    description: 'Last name when present on the event payload.',
    exampleValue: 'Doe',
  },
  {
    key: 'createdAt',
    type: 'int',
    required: false,
    description:
      'Unix timestamp (seconds) of the underlying Clerk object when available.',
    exampleValue: 1_716_883_200,
  },
]

function buildPayloadSchema(
  extraFields: ReadonlyArray<TriggerField>
): TriggerPayloadSchema {
  return {
    type: 'object',
    fields: [...COMMON_FIELDS, ...extraFields],
  }
}

/**
 * Slim sample seed for the Test dialog. Only the user-facing common
 * fields are inlined here; per-event `samplePayload` calls below add
 * the event-specific keys. The `eventType` arg is kept for the
 * signature symmetry with the other provider catalogs even though the
 * slim shape no longer echoes it.
 */
function buildSamplePayload(
  _eventType: SupportedClerkEventType,
  payload: Record<string, string | number | boolean | null>
): Record<string, string | number | boolean | null> {
  return {
    email: 'jane.doe@acme.com',
    firstName: 'Jane',
    lastName: 'Doe',
    ...payload,
  }
}

export const CLERK_EVENT_METADATA: Array<ClerkSupportedEventMetadata> = [
  {
    eventType: 'user.created',
    title: 'Clerk - User Created',
    description:
      'Fires when a new Clerk user signs up. Use this to send a welcome email or sync the user into Brew contacts.',
    category: 'user',
    payloadSchema: buildPayloadSchema(USER_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('user.created', {
      firstName: 'John',
      lastName: 'Doe',
      username: 'jane',
      imageUrl: 'https://img.clerk.com/xxxxxx',
      lastSignInAt: 1_716_883_200,
      createdAt: 1_716_883_200,
    }),
    useCases: [
      {
        title: 'Send a welcome email',
        description:
          'Greet brand-new signups with onboarding next steps and a setup checklist.',
      },
      {
        title: 'Sync to Brew contacts',
        description:
          'Add the new user to the Brew contact list for downstream segmentation.',
      },
    ],
  },
  {
    eventType: 'organization.created',
    title: 'Clerk - Organization Created',
    description:
      'Receives a new Clerk organization. Brew resolves `created_by` through the tenant identity ledger. If that user has not supplied a real email on an earlier event, Brew records `unresolved_recipient` and starts no automation.',
    category: 'organization',
    payloadSchema: buildPayloadSchema(ORG_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('organization.created', {
      email: 'unknown+org_2g7np7Hrk0SN6kj5EDMLDaKNL0S@org_test.brew.local',
      name: 'Acme Corp',
      slug: 'acme-corp',
      membersCount: 1,
      maxAllowedMemberships: 5,
      createdAt: 1_716_883_200,
    }),
    useCases: [
      {
        title: 'Resolved creator onboarding',
        description:
          'Send a workspace-setup checklist when Brew has previously observed the creator email.',
      },
      {
        title: 'Organization segmentation',
        description:
          'Branch on organization name, slug, and seat limits after the creator identity resolves.',
      },
    ],
  },
  {
    eventType: 'organizationInvitation.created',
    title: 'Clerk - Organization Invitation Sent',
    description:
      'Fires when an admin sends an organization invitation (status `pending`). Use to deliver the "you have been invited" email to the recipient or to notify the inviter that the invite went out.',
    category: 'organization',
    payloadSchema: buildPayloadSchema(ORG_INVITATION_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('organizationInvitation.created', {
      email: 'jane.doe@acme.com',
      roleName: 'Member',
      status: 'pending',
      expiresAt: 1_717_487_999,
      createdAt: 1_716_883_200,
    }),
    useCases: [
      {
        title: 'Invitation email to the recipient',
        description:
          'Deliver a branded "you have been invited" email with the org name, inviter, and accept link.',
      },
      {
        title: 'Confirm to the inviter',
        description:
          'Notify the admin that their invitation was sent successfully so they can chase it up later if needed.',
      },
    ],
  },
  {
    eventType: 'organizationInvitation.accepted',
    title: 'Clerk - Organization Invitation Accepted',
    description:
      'Fires when an organization invitation is accepted. Use to welcome the new member or notify the inviter.',
    category: 'organization',
    payloadSchema: buildPayloadSchema(ORG_INVITATION_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('organizationInvitation.accepted', {
      email: 'jane.doe@acme.com',
      roleName: 'Member',
      status: 'accepted',
      expiresAt: 1_717_487_999,
      createdAt: 1_716_883_200,
    }),
    useCases: [
      {
        title: 'New-member welcome',
        description:
          'Greet the newly joined member with workspace orientation and a quick "what to try first" guide.',
      },
      {
        title: 'Notify the inviter',
        description:
          'Let the original inviter know their teammate has joined so they can hand off shared work.',
      },
    ],
  },
  {
    eventType: 'organizationInvitation.revoked',
    title: 'Clerk - Organization Invitation Revoked',
    description:
      'Fires when an admin revokes a pending organization invitation. Use to inform the prospective member that their invite is no longer valid or to update internal CRM state.',
    category: 'organization',
    payloadSchema: buildPayloadSchema(ORG_INVITATION_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('organizationInvitation.revoked', {
      email: 'jane.doe@acme.com',
      roleName: 'Member',
      status: 'revoked',
      expiresAt: 1_717_487_999,
      createdAt: 1_716_883_200,
    }),
    useCases: [
      {
        title: 'Notify the prospective member',
        description:
          'Send a courteous "your invitation was withdrawn" email so the recipient is not left clicking a dead invite link.',
      },
      {
        title: 'Update internal records',
        description:
          'Roll back any sales / CRM record that anticipated the user joining the org so reporting stays accurate.',
      },
    ],
  },
  {
    eventType: 'organizationMembership.created',
    title: 'Clerk - Organization Membership Created',
    description:
      'Fires when a user is added to a Clerk organization (either by accepting an invitation or by direct add). Carries the member identity + role.',
    category: 'organization',
    payloadSchema: buildPayloadSchema(MEMBERSHIP_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('organizationMembership.created', {
      email: 'jane.doe@acme.com',
      firstName: 'Jane',
      lastName: 'Doe',
      organizationName: 'Acme Corp',
      organizationSlug: 'acme-corp',
      roleName: 'Member',
      createdAt: 1_716_883_200,
    }),
    useCases: [
      {
        title: 'Team kickoff email',
        description:
          'Email the new member with their role, who else is on the team, and shared docs.',
      },
      {
        title: 'Admin audit',
        description:
          'Notify org admins (via a webhook or admin email) when a new member is added so they can review access.',
      },
    ],
  },
  {
    eventType: 'paymentAttempt.created',
    title: 'Clerk - Payment Attempt Created',
    description:
      'Fires when Clerk Billing starts charging the customer. Use to send pre-emptive billing notices or to record the attempt in your CRM.',
    category: 'billing',
    payloadSchema: buildPayloadSchema(PAYMENT_ATTEMPT_FIELDS),
    samplePayload: buildSamplePayload('paymentAttempt.created', {
      email: 'john.doe@acme.com',
      firstName: 'John',
      lastName: 'Doe',
      status: 'pending',
      chargeType: 'recurring',
      grandTotalAmount: 2200,
      grandTotalFormatted: '$22.00',
      currency: 'USD',
      billingDate: 1_716_883_200,
      createdAt: 1_716_883_200,
    }),
    useCases: [
      {
        title: 'Pre-charge heads-up',
        description:
          'Send a "we are about to charge your card" email to reduce surprise chargebacks.',
      },
      {
        title: 'Failed-payment alert',
        description:
          'When `status === "failed"`, drip a dunning sequence with one-click update-card links.',
      },
    ],
  },
  {
    eventType: 'subscription.created',
    title: 'Clerk - Subscription Created',
    description:
      'Fires when a new Clerk Billing subscription is created. Use to send a "thanks for subscribing" email with plan details.',
    category: 'billing',
    payloadSchema: buildPayloadSchema(SUBSCRIPTION_FIELDS),
    samplePayload: buildSamplePayload('subscription.created', {
      email: 'test@example.com',
      firstName: 'Test',
      lastName: 'User',
      status: 'active',
      activeAt: 1_716_883_200,
      planName: 'Pro Plan',
      planSlug: 'pro-plan',
      planAmount: 2000,
      currency: 'USD',
      periodStart: 1_716_883_200,
      periodEnd: 1_719_561_600,
      createdAt: 1_716_883_200,
    }),
    useCases: [
      {
        title: 'Welcome to the plan',
        description:
          'Send a personalized welcome email with onboarding for plan-specific features.',
      },
      {
        title: 'Receipt + tax compliance',
        description:
          'Email the subscriber a templated receipt with the plan name, amount, and next billing date.',
      },
    ],
  },
  {
    eventType: 'subscriptionItem.freeTrialEnding',
    title: 'Clerk - Subscription Item Free Trial Ending',
    description:
      'Fires shortly before a Clerk Billing free trial converts to a paid subscription. Use to remind the customer their card will be charged.',
    category: 'billing',
    payloadSchema: buildPayloadSchema(SUBSCRIPTION_ITEM_FIELDS),
    samplePayload: buildSamplePayload('subscriptionItem.freeTrialEnding', {
      email: 'test@example.com',
      firstName: 'Test',
      lastName: 'User',
      planName: 'Premium Plan with Trial',
      planSlug: 'premium-trial',
      planAmount: 2999,
      currency: 'USD',
      status: 'active',
      isFreeTrial: true,
      periodStart: 1_716_883_200,
      periodEnd: 1_719_561_600,
      updatedAt: 1_716_883_200,
      createdAt: 1_716_883_200,
    }),
    useCases: [
      {
        title: 'Trial-ending reminder',
        description:
          'Email the user 3 days before conversion with the price and a link to cancel or pick a different plan.',
      },
      {
        title: 'Conversion nudge',
        description:
          'Highlight 1-2 paid-only features they’ve already used in the trial to encourage staying.',
      },
    ],
  },
  {
    eventType: 'subscriptionItem.pastDue',
    title: 'Clerk - Subscription Item Past Due',
    description:
      'Fires when a Clerk Billing subscription item misses payment. Use to start a dunning sequence (update card, soft-suspend access).',
    category: 'billing',
    payloadSchema: buildPayloadSchema(SUBSCRIPTION_ITEM_FIELDS),
    samplePayload: buildSamplePayload('subscriptionItem.pastDue', {
      email: 'test@example.com',
      firstName: 'Test',
      lastName: 'User',
      planName: 'Pro Plan',
      planSlug: 'pro-plan',
      planAmount: 2000,
      currency: 'USD',
      status: 'past_due',
      isFreeTrial: false,
      periodStart: 1_716_883_200,
      periodEnd: 1_719_561_600,
      updatedAt: 1_716_969_600,
      createdAt: 1_716_883_200,
    }),
    useCases: [
      {
        title: 'Dunning sequence',
        description:
          'Send an "update your card" email with a one-click recovery link, then escalate over 7 days.',
      },
      {
        title: 'Internal billing alert',
        description:
          'Notify your CS team when a high-MRR customer goes past due so they can reach out personally.',
      },
    ],
  },
  {
    eventType: 'subscriptionItem.canceled',
    title: 'Clerk - Subscription Item Canceled',
    description:
      'Fires when a Clerk Billing subscription item is canceled (user-initiated or admin action). Use to send a win-back or exit-survey email.',
    category: 'billing',
    payloadSchema: buildPayloadSchema(SUBSCRIPTION_ITEM_FIELDS),
    samplePayload: buildSamplePayload('subscriptionItem.canceled', {
      email: 'test@example.com',
      firstName: 'Test',
      lastName: 'User',
      planName: 'Pro Plan',
      planSlug: 'pro-plan',
      planAmount: 2000,
      currency: 'USD',
      status: 'canceled',
      isFreeTrial: false,
      periodStart: 1_716_883_200,
      periodEnd: 1_719_561_600,
      updatedAt: 1_716_969_600,
      createdAt: 1_716_883_200,
    }),
    useCases: [
      {
        title: 'Exit survey',
        description:
          'Email the cancelled user a one-question survey to understand why and surface offers if relevant.',
      },
      {
        title: 'Win-back drip',
        description:
          'Schedule a 30 / 60 / 90-day re-engagement sequence with new-feature highlights and a comeback discount.',
      },
    ],
  },
  {
    eventType: 'subscriptionItem.abandoned',
    title: 'Clerk - Subscription Item Abandoned',
    description:
      'Fires when a Clerk Billing subscription item is abandoned (typically: trial ended without payment-method on file). Treat as a "trial-ended-no-convert" signal.',
    category: 'billing',
    payloadSchema: buildPayloadSchema(SUBSCRIPTION_ITEM_FIELDS),
    samplePayload: buildSamplePayload('subscriptionItem.abandoned', {
      email: 'test@example.com',
      firstName: 'Test',
      lastName: 'User',
      planName: 'Pro Plan',
      planSlug: 'pro-plan',
      planAmount: 2000,
      currency: 'USD',
      status: 'abandoned',
      isFreeTrial: false,
      periodStart: 1_716_883_200,
      periodEnd: 1_719_561_600,
      updatedAt: 1_716_969_600,
      createdAt: 1_716_883_200,
    }),
    useCases: [
      {
        title: 'Last-chance offer',
        description:
          'Send a discount or extended-trial offer the moment a trial expires without a payment method.',
      },
      {
        title: 'Mark dormant in CRM',
        description:
          'Tag the contact as "trial-no-convert" so your sales team can follow up at the right cadence.',
      },
    ],
  },
  {
    eventType: 'waitlistEntry.created',
    title: 'Clerk - Waitlist Entry Created',
    description:
      'Fires when a new email is added to the Clerk waitlist. Use to send a "you’re on the list" email and start a nurture sequence.',
    category: 'waitlist',
    payloadSchema: buildPayloadSchema(WAITLIST_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('waitlistEntry.created', {
      email: 'example@example.org',
      status: 'pending',
      createdAt: 1_736_332_397,
    }),
    useCases: [
      {
        title: 'You’re on the list',
        description:
          'Confirm the signup, set expectations on next steps, and tease an early-access perk.',
      },
      {
        title: 'Nurture drip',
        description:
          'Kick off a weekly drip with launch progress, screenshots, and an unlock-priority referral CTA.',
      },
    ],
  },
]
